import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
// Types
import {
  SortDirection,
  StreamOrderBy,
  StreamStatus,
  TicketEventsInput,
  TicketEventsOrderBy,
  TicketEventStatus,
  TicketEventTiming,
  TicketHoldItemInput,
  TicketOrderStatus,
  TicketTierSaleState,
} from 'api/graphql-global-types';
import { GetTicketAvailability_ticketAvailability } from 'api/ticketEvents/types/GetTicketAvailability';
import { GetTicketServiceFee_ticketServiceFee } from 'api/ticketEvents/types/GetTicketServiceFee';
import { PublicTicketEventFields } from 'api/ticketEvents/types/PublicTicketEventFields';
import { TicketHoldFields_lines } from 'api/ticketEvents/types/TicketHoldFields';
// Constants
import { EVENT_TICKETS } from 'constants/routes';
import {
  TICKET_EVENT_POSTER_SIZES,
  TICKET_EVENT_POSTER_WIDTHS,
  TICKET_SALES_ENDING_SOON_HOURS,
  TICKET_SEATS_LEFT_THRESHOLD,
} from 'constants/ticketEvents';
// Helpers
import { buildImageFetchSrcSet } from 'helpers/cdn';
import { ImagePreload } from 'helpers/profileAvatar';
import { computeOrderStreamPath, getEnvLink } from 'helpers/routes';

dayjs.extend(utc);
dayjs.extend(timezone);

export type TicketTier = GetTicketAvailability_ticketAvailability;
/** Tickets picked per tier, keyed by tier id. */
export type TicketQuantities = Record<string, number>;

export type TicketSelectionLine = {
  tierId: string;
  label: string;
  quantity: number;
  lineTotal: number;
};

export type TicketOrderAmounts = {
  subtotal: number;
  discountAmount: number;
  serviceFeeAmount: number;
  total: number;
};

export type TicketTierBadge = { text: string; variant: 'error' | 'muted' };

const toCents = (dollars: number): number => Math.round(Number(dollars) * 100);

export const isTierOnSale = (tier: TicketTier): boolean =>
  tier.saleState === TicketTierSaleState.OnSale && tier.availableAdmissions > 0;

/** Most a buyer can add from one tier: its per-order cap, or what is left. */
export const getTierMaxQuantity = (tier: TicketTier): number =>
  isTierOnSale(tier) ? Math.min(tier.maxPerOrder, tier.availableAdmissions) : 0;

const TIER_STATE_BADGES: Partial<Record<TicketTierSaleState, string>> = {
  [TicketTierSaleState.NotYetOnSale]: 'Not On Sale Yet',
  [TicketTierSaleState.SalesEnded]: 'Sales Ended',
  [TicketTierSaleState.SalesPaused]: 'Sales Paused',
};

export const isTierSoldOut = (tier: TicketTier): boolean =>
  tier.saleState === TicketTierSaleState.SoldOut ||
  tier.availableAdmissions <= 0;

/** Every tier is gone; an event without tiers is not called sold out. */
export const areAllTiersSoldOut = (tiers: TicketTier[]): boolean =>
  tiers.length > 0 && tiers.every(isTierSoldOut);

export const getTierBadge = (tier: TicketTier): TicketTierBadge | null => {
  if (isTierSoldOut(tier)) {
    return { text: 'Sold Out', variant: 'muted' };
  }

  const stateBadge = TIER_STATE_BADGES[tier.saleState];

  if (stateBadge) {
    return { text: stateBadge, variant: 'muted' };
  }

  if (tier.availableAdmissions <= TICKET_SEATS_LEFT_THRESHOLD) {
    const seats = tier.availableAdmissions === 1 ? 'Seat' : 'Seats';

    return {
      text: `${tier.availableAdmissions} ${seats} Left`,
      variant: 'error',
    };
  }

  return null;
};

export const getTierSalesEndText = (
  tier: TicketTier,
  now: dayjs.Dayjs = dayjs()
): string | null => {
  if (!isTierOnSale(tier) || !tier.salesCloseAt) {
    return null;
  }

  const minutesLeft = dayjs(tier.salesCloseAt).diff(now, 'minute');

  if (minutesLeft <= 0 || minutesLeft > TICKET_SALES_ENDING_SOON_HOURS * 60) {
    return null;
  }

  if (minutesLeft < 60) {
    return `Sales end in ${minutesLeft} ${
      minutesLeft === 1 ? 'minute' : 'minutes'
    }`;
  }

  const hoursLeft = Math.floor(minutesLeft / 60);

  return `Sales end in ${hoursLeft} ${hoursLeft === 1 ? 'hour' : 'hours'}`;
};

/** Any tier still selling that is down to its last few seats. */
export const hasFewTicketsLeft = (tiers: TicketTier[]): boolean =>
  tiers.some(
    (tier) =>
      isTierOnSale(tier) &&
      tier.availableAdmissions <= TICKET_SEATS_LEFT_THRESHOLD
  );

export const getSelectionLines = (
  tiers: TicketTier[],
  quantities: TicketQuantities
): TicketSelectionLine[] =>
  tiers
    .filter((tier) => (quantities[tier.tierId] ?? 0) > 0)
    .map((tier) => {
      const quantity = quantities[tier.tierId];

      return {
        tierId: tier.tierId,
        label: tier.tierLabel,
        quantity,
        lineTotal: (toCents(tier.price) * quantity) / 100,
      };
    });

/** The checkout rows as the hold priced them, named after their tiers. */
export const getHoldSelectionLines = (
  tiers: TicketTier[],
  holdLines: TicketHoldFields_lines[]
): TicketSelectionLine[] =>
  holdLines.map((line) => ({
    tierId: line.tierId,
    label:
      tiers.find((tier) => tier.tierId === line.tierId)?.tierLabel ?? 'Tickets',
    quantity: line.quantity,
    lineTotal: line.lineTotal,
  }));

/** Keeps each pick within what its tier still sells, dropping tiers that stopped selling. */
export const clampTicketQuantities = (
  tiers: TicketTier[],
  quantities: TicketQuantities
): TicketQuantities =>
  tiers.reduce<TicketQuantities>((clamped, tier) => {
    const quantity = Math.min(
      quantities[tier.tierId] ?? 0,
      getTierMaxQuantity(tier)
    );

    return quantity > 0 ? { ...clamped, [tier.tierId]: quantity } : clamped;
  }, {});

export const toTicketHoldItems = (
  quantities: TicketQuantities
): TicketHoldItemInput[] =>
  Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([tierId, quantity]) => ({ tierId, quantity }));

/**
 * What the buyer will pay before a hold prices it, worked out the way the server does: the fee is a percent of the
 * subtotal plus a flat amount per ticket, in whole cents.
 */
export const estimateTicketOrder = (
  lines: Pick<TicketSelectionLine, 'quantity' | 'lineTotal'>[],
  fee: Pick<
    GetTicketServiceFee_ticketServiceFee,
    'percent' | 'perTicket'
  > | null
): TicketOrderAmounts => {
  const subtotal = lines.reduce(
    (sum, line) => sum + toCents(line.lineTotal),
    0
  );
  const tickets = lines.reduce((sum, line) => sum + line.quantity, 0);
  const serviceFeeAmount =
    subtotal && fee
      ? Math.round((subtotal * fee.percent) / 100) +
        toCents(fee.perTicket) * tickets
      : 0;

  return {
    subtotal: subtotal / 100,
    discountAmount: 0,
    serviceFeeAmount: serviceFeeAmount / 100,
    total: (subtotal + serviceFeeAmount) / 100,
  };
};

/** "jane.doe@example.com" → "j****d****@example.com" */
export const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');

  if (!domain) {
    return email;
  }

  const masked = localPart
    .split('.')
    .map((part) => `${part.slice(0, 1)}****`)
    .join('');

  return `${masked}@${domain}`;
};

/** Tickets can be bought now: the event sells them, is on sale and its sales window is still open. */
export const isTicketEventSelling = (
  event: Pick<
    PublicTicketEventFields,
    'status' | 'ticketsEnabled' | 'salesCloseAt'
  >,
  now: Date = new Date()
): boolean =>
  event.ticketsEnabled &&
  event.status === TicketEventStatus.OnSale &&
  new Date(event.salesCloseAt).getTime() > now.getTime();

const TICKET_SALES_STOPPED_TEXT: Partial<Record<TicketEventStatus, string>> = {
  [TicketEventStatus.Paused]: 'Ticket sales are paused',
  [TicketEventStatus.Postponed]: 'This event is postponed',
};

/** Why a published event is not selling tickets right now. */
export const getTicketSalesStoppedText = (
  event: Pick<PublicTicketEventFields, 'status'>
): string =>
  TICKET_SALES_STOPPED_TEXT[event.status] ?? 'Ticket sales are closed';

type TicketEventPathFields = {
  slug: string;
  stream: { slug: string; store: { slug: string | null } | null } | null;
};

export const getTicketEventPath = (event: TicketEventPathFields): string =>
  event.stream?.slug && event.stream.store?.slug
    ? computeOrderStreamPath(event.stream)
    : `${EVENT_TICKETS}/${event.slug}`;

export const getTicketEventUrl = (event: TicketEventPathFields): string =>
  `${getEnvLink()}${getTicketEventPath(event)}`;

/** The pay-per-view price of the stream the event is broadcast on, if it has one. */
export const getTicketEventPpvPrice = (
  event: Pick<PublicTicketEventFields, 'stream'>
): number | null =>
  event.stream && !event.stream.isFree && event.stream.price > 0
    ? event.stream.price
    : null;

/** "Barclays Center, Brooklyn" */
export const getTicketEventPlace = (event: {
  venueName: string;
  venueAddress: { city: string } | null;
}): string =>
  [event.venueName, event.venueAddress?.city].filter(Boolean).join(', ');

/** "Barclays Center, 620 Atlantic Ave, Brooklyn, NY" */
export const getTicketEventFullAddress = (
  event: Pick<PublicTicketEventFields, 'venueName' | 'venueAddress'>
): string =>
  [
    event.venueName,
    event.venueAddress?.addressLine1,
    event.venueAddress?.city,
    event.venueAddress?.region,
  ]
    .filter(Boolean)
    .join(', ');

const inEventZone = (date: string, zone: string): dayjs.Dayjs =>
  zone ? dayjs(date).tz(zone) : dayjs(date);

/** "5 PM", or "5:30 PM" off the hour. */
const formatEventHour = (date: dayjs.Dayjs): string =>
  date.format(date.minute() ? 'h:mm A' : 'h A');

/** "SAT 16 AUG 08:00PM", in the venue's time zone. */
export const formatTicketEventTagDate = (
  startsAt: string,
  zone: string
): string =>
  inEventZone(startsAt, zone).format('ddd DD MMM hh:mmA').toUpperCase();

/** "16 Oct 2026 at 05:00 PM", in the venue's time zone. */
export const formatTicketEventCardDate = (
  startsAt: string,
  zone: string
): string => inEventZone(startsAt, zone).format('D MMM YYYY [at] hh:mm A');

/** "Thu, Oct 8 • 5 PM", in the venue's time zone. */
export const formatTicketEventShortDate = (
  startsAt: string,
  zone: string
): string => {
  const start = inEventZone(startsAt, zone);

  return `${start.format('ddd, MMM D')} • ${formatEventHour(start)}`;
};

const formatTimeZoneName = (
  date: Date,
  zone: string,
  timeZoneName: Intl.DateTimeFormatOptions['timeZoneName']
): string | undefined =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: zone || undefined,
    timeZoneName,
  })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;

/** "ET" rather than "EDT"/"EST", whatever the season; browsers without 'shortGeneric' (Safari < 15.4) get "EDT". */
export const getTimeZoneShortName = (
  date: Date,
  zone: string
): string | undefined => {
  try {
    return formatTimeZoneName(
      date,
      zone,
      'shortGeneric' as Intl.DateTimeFormatOptions['timeZoneName']
    );
  } catch {
    return formatTimeZoneName(date, zone, 'short');
  }
};

/** The start in the venue's time zone, followed by the zone's short name. */
export const formatTicketEventDateWithZone = (
  startsAt: string,
  zone: string,
  format: string
): string => {
  const start = inEventZone(startsAt, zone);

  return [start.format(format), getTimeZoneShortName(start.toDate(), zone)]
    .filter(Boolean)
    .join(' ');
};

/** "04/20/2026 08:00PM ET", in the venue's time zone. */
export const formatTicketEventTableDate = (
  startsAt: string,
  zone: string
): string => formatTicketEventDateWithZone(startsAt, zone, 'MM/DD/YYYY hh:mmA');

/** "April 20, 2026 08:00 PM ET" */
export const formatTicketEventLongDate = (
  startsAt: string,
  zone: string
): string =>
  formatTicketEventDateWithZone(startsAt, zone, 'MMMM D, YYYY hh:mm A');

/** "Thursday, October 8  •  5 PM - 10 PM" */
export const formatTicketEventSchedule = (
  startsAt: string,
  endsAt: string,
  zone: string
): string => {
  const start = inEventZone(startsAt, zone);
  const end = inEventZone(endsAt, zone);

  return `${start.format('dddd, MMMM D')}  •  ${formatEventHour(
    start
  )} - ${formatEventHour(end)}`;
};

export const TICKET_ORDER_FAILED_STATUSES = [
  TicketOrderStatus.Cancelled,
  TicketOrderStatus.Expired,
];

/** The ticketing error code the API attaches to a refused request. */
export const getTicketingErrorCode = (error: unknown): string | undefined =>
  // The API error shape is untyped.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (error as any)?.graphQLErrors?.[0]?.extensions?.response?.ticketCode;

const STREAM_STATUS_TIMINGS: Partial<
  Record<StreamStatus, TicketEventTiming>
> = {
  [StreamStatus.Active]: TicketEventTiming.Live,
  [StreamStatus.Scheduled]: TicketEventTiming.Upcoming,
  [StreamStatus.Ended]: TicketEventTiming.Past,
};

const SORT_ORDERS: Partial<Record<string, TicketEventsOrderBy>> = {
  [StreamOrderBy.Price]: TicketEventsOrderBy.TicketPrice,
  [StreamOrderBy.Name]: TicketEventsOrderBy.Name,
  [StreamOrderBy.ScheduleDate]: TicketEventsOrderBy.StartsAt,
  [StreamOrderBy.ScheduleDateStrict]: TicketEventsOrderBy.StartsAt,
};

export type BuildTicketEventsInputParams = {
  priceRange?: [number, number];
  streamStatus?: StreamStatus[];
  dateRange?: [string, string];
  searchTerm?: string;
  storeIds?: string[];
  /** Leaves out events sold with a stream, which list with the streams. */
  standaloneOnly?: boolean;
  orderBy?: string;
  direction?: SortDirection;
  offset?: number;
  limit?: number;
};

export const TICKET_EVENTS_INITIAL_SORTING = {
  direction: SortDirection.ASC,
  order: StreamOrderBy.ScheduleDate,
};

/** The Watch page filters, read for the Events tab. */
export const buildTicketEventsInput = ({
  priceRange,
  streamStatus,
  dateRange,
  searchTerm,
  storeIds,
  standaloneOnly,
  orderBy,
  direction = SortDirection.ASC,
  offset = 0,
  limit = 16,
}: BuildTicketEventsInputParams): TicketEventsInput => {
  const timings = (streamStatus ?? [])
    .map((status) => STREAM_STATUS_TIMINGS[status])
    .filter((timing): timing is TicketEventTiming => Boolean(timing));

  return {
    offset,
    limit,
    ticketsOnly: true,
    orderBy: (orderBy && SORT_ORDERS[orderBy]) || TicketEventsOrderBy.StartsAt,
    direction,
    ...(timings.length && { timings }),
    ...(searchTerm?.trim() && { searchTerm: searchTerm.trim() }),
    ...(storeIds?.length && { storeIds }),
    ...(standaloneOnly && { standaloneOnly }),
    ...(priceRange && { priceFrom: priceRange[0], priceTo: priceRange[1] }),
    ...(dateRange && { startsFrom: dateRange[0], startsTo: dateRange[1] }),
  };
};

/** The events list's first page before any filter, which the page head also lists. */
export const getUnfilteredTicketEventsInput = (
  storeId?: string
): TicketEventsInput =>
  buildTicketEventsInput({
    ...(storeId && { storeIds: [storeId], standaloneOnly: true }),
    orderBy: TICKET_EVENTS_INITIAL_SORTING.order,
    direction: TICKET_EVENTS_INITIAL_SORTING.direction,
  });

/** Lets the head preload fetch the same resized poster the event hero picks. */
export const getTicketEventPosterPreload = (
  coverImageUrl: string | null
): ImagePreload | undefined => {
  const srcSet = coverImageUrl
    ? buildImageFetchSrcSet(coverImageUrl, TICKET_EVENT_POSTER_WIDTHS)
    : undefined;

  return srcSet ? { srcSet, sizes: TICKET_EVENT_POSTER_SIZES } : undefined;
};
